import { getDeviceSocketUuid } from './resolveDeviceChatIds'
import { MESSAGE_CATEGORY_IDS, resolveMessageCategoryId } from '../../constants/messageCategoryIds'

export const SOCKET_MASS_MESSAGE_TARGET = 'massmessage'

/**
 * Numeric 0 = “all” in legacy socket service (`tools.empty(0)` skips DB filters).
 * Also used so broadcast push notifications reach every device (massmessage loop).
 */
export const SOCKET_BROADCAST_ALL_SCOPE = 0

/**
 * Legacy `type_message` values understood by wss.socket-service-next messages middleware.
 * The server uses these to decide delivery: '1' is treated as an INBOUND message coming
 * FROM a device (so it skips device push + SMS), while operator-originated sends must use
 * '2' (to a single device) or '3' (mass message) to trigger push and SMS fallback.
 * See messages/middlewares.js (SMS ~L729, sendPushNotifications ~L1330).
 */
export const SOCKET_MESSAGE_TYPES = {
  DEVICE_RESPONSE: '1',
  OPERATOR_TO_DEVICE: '2',
  MASS_MESSAGE: '3',
  WARNING: '4'
}

/**
 * Legacy socket payload for messages::create (see wss.socket-service-next messages/schemas.js).
 *
 * Delivery semantics (decided by the server from `type_message`):
 *  - single device  -> '2' (push + SMS fallback)
 *  - true broadcast  -> '3' + massmessage target + all-scope (push to every device)
 *  - user-only thread -> preserved legacy behaviour (massmessage / '1'); the legacy
 *    device-based protocol cannot address a user directly. NOTE: this still fans out
 *    to all device inboxes without push, same as before — tracked as a separate gap,
 *    intentionally left unchanged here so this fix never escalates a private reply
 *    into a push-broadcast.
 *
 * @param {import('../../redux/models/Message').default} storeMessage
 * @param {{ uuidDevice?: string, uuid?: string, name?: string } | null} targetDevice
 * @param {{ categoryName?: string } | null} options
 */
const newTempUuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })

export function normalizeMessageCategoryName (categoryName) {
  return String(categoryName || '').trim().toLowerCase()
}

/**
 * Legacy warning messages (old Polymer chat): type_message 4, system sender.
 * Mobile/iOS paint yellow when type_message === "4" (not message_color).
 */
export function buildWarningSocketMessagePayload (storeMessage, targetDevice = null) {
  const tempUuid = newTempUuid()
  const targetUuid = getDeviceSocketUuid(targetDevice) || storeMessage.receiverDeviceId
  return {
    uuid_device: 'system',
    name_device: 'Warning system',
    uuid_device_target: targetUuid,
    name_device_target: targetDevice?.name || undefined,
    body_message: storeMessage.content,
    type_message: SOCKET_MESSAGE_TYPES.WARNING,
    is_chat_message: 1,
    temp_uuid: tempUuid
  }
}

export function buildSocketMessageCreatePayload (storeMessage, targetDevice = null, options = null) {
  const tempUuid = newTempUuid()
  const categoryName = normalizeMessageCategoryName(options?.categoryName)
  const categoryId = resolveMessageCategoryId({
    categoryId: storeMessage.categoryId ?? options?.categoryId,
    categoryName
  })

  if (storeMessage.receiverDeviceId) {
    const targetUuid = getDeviceSocketUuid(targetDevice) || storeMessage.receiverDeviceId
    if (categoryId === MESSAGE_CATEGORY_IDS.WARNING || categoryName === 'warning') {
      return buildWarningSocketMessagePayload(storeMessage, targetDevice)
    }
    return {
      uuid_device: 'operator',
      uuid_device_target: targetUuid,
      body_message: storeMessage.content,
      type_message: SOCKET_MESSAGE_TYPES.OPERATOR_TO_DEVICE,
      name_device_target: targetDevice?.name || undefined,
      is_chat_message: 1,
      temp_uuid: tempUuid,
      categoryId,
      ...(categoryId === MESSAGE_CATEGORY_IDS.DANGER || categoryName === 'danger'
        ? { danger_message: 1 }
        : {})
    }
  }

  // Only a thread with neither a device nor a user is a real "broadcast to all".
  const isBroadcast = !storeMessage.receiverUserId

  return {
    uuid_device: 'operator',
    uuid_device_target: SOCKET_MASS_MESSAGE_TARGET,
    body_message: storeMessage.content,
    type_message: isBroadcast
      ? SOCKET_MESSAGE_TYPES.MASS_MESSAGE
      : SOCKET_MESSAGE_TYPES.DEVICE_RESPONSE,
    name_device_target: targetDevice?.name || undefined,
    is_chat_message: 1,
    temp_uuid: tempUuid,
    categoryId,
    ...(storeMessage.receiverUserId
      ? { receiver_user_id: storeMessage.receiverUserId }
      : {}),
    ...(categoryName && categoryName !== 'info' ? { categoryName } : {}),
    ...(categoryId === MESSAGE_CATEGORY_IDS.DANGER || categoryName === 'danger'
      ? { danger_message: 1 }
      : {}),
    // Broadcast to every device regardless of headquarter / status.
    ...(isBroadcast
      ? {
          id_headquarter: SOCKET_BROADCAST_ALL_SCOPE,
          status_device: SOCKET_BROADCAST_ALL_SCOPE
        }
      : {})
  }
}
